import React, { useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import api from "../../../utils/api";
import { PermissionsContext } from "../../../utils/PermissionsContext";

import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TimerIcon from "@mui/icons-material/Timer";
import TimerOffIcon from "@mui/icons-material/TimerOff";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";

const TimeLogsTab = ({ workOrderId, currentUserId, canEdit }) => {
  const permissions = useContext(PermissionsContext);
  const hasPermission = (perm) => permissions?.includes(perm);

  const [timeLogs, setTimeLogs] = useState([]);
  const [summary, setSummary] = useState({
    total_entries: 0,
    total_hours: 0,
    billable_hours: 0,
    total_billable_amount: 0,
    billing_rule_rate: null,
    billing_code: null,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Billing rule for this WO. fetched once on mount
  const [billingRule, setBillingRule] = useState(null); // { time_rate, time_billing_code, bill_time }

  const [openDialog, setOpenDialog] = useState(false);
  const [editingLog, setEditingLog] = useState(null);

  const [form, setForm] = useState({
    log_date: dayjs().format("YYYY-MM-DD"),
    start_time: "",
    end_time: "",
    hours: "",
    description: "",
    is_billable: true,
    // manual_billing_amount is only used when no fixed rule rate exists
    manual_billing_amount: "",
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [clockedIn, setClockedIn] = useState(false);
  const [clockSession, setClockSession] = useState(null);
  const [clockingOut, setClockingOut] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchTimeLogs = async () => {
    try {
      const [logsRes, summaryRes] = await Promise.all([
        api.get(`/workorder-time-logs/${workOrderId}`),
        api.get(`/workorder-time-logs-summary/${workOrderId}`),
      ]);

      setTimeLogs(logsRes.data || []);
      setSummary(
        summaryRes.data || {
          total_entries: 0,
          total_hours: 0,
          billable_hours: 0,
          total_billable_amount: 0,
        }
      );
    } catch (err) {
      console.error("❌ Failed to fetch time logs:", err);
      showSnackbar("❌ Failed to load time logs.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Check active clock session
  useEffect(() => {
    if (!workOrderId) return;
    api.get(`/work-orders/${workOrderId}/active-session`).then(r => {
      if (r.data) { setClockedIn(true); setClockSession(r.data); }
      else { setClockedIn(false); setClockSession(null); }
    }).catch(() => {});
  }, [workOrderId]);

  const handleClockIn = async () => {
    try {
      await api.post(`/work-orders/${workOrderId}/clock-in`);
      setClockedIn(true);
      const r = await api.get(`/work-orders/${workOrderId}/active-session`);
      setClockSession(r.data);
      setSnackbarMessage("Clocked in - timer started.");
      setSnackbarSeverity("success"); setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage(err.response?.data?.message || "Clock-in failed.");
      setSnackbarSeverity("error"); setSnackbarOpen(true);
    }
  };

  const handleClockOut = async () => {
    setClockingOut(true);
    try {
      const res = await api.post(`/work-orders/${workOrderId}/clock-out`, { description:"Clocked session", is_billable: true });
      setClockedIn(false); setClockSession(null);
      setSnackbarMessage(`Clocked out: ${res.data.hours}h logged automatically.`);
      setSnackbarSeverity("success"); setSnackbarOpen(true);
      // Reload time logs
      const [tlRes, sumRes] = await Promise.all([
        api.get(`/workorder-time-logs/${workOrderId}`),
        api.get(`/workorder-time-logs-summary/${workOrderId}`)
      ]);
      if (tlRes.data) setTimeLogs(tlRes.data);
      if (sumRes.data) setSummary(sumRes.data);
    } catch (err) {
      setSnackbarMessage(err.response?.data?.message || "Clock-out failed.");
      setSnackbarSeverity("error"); setSnackbarOpen(true);
    } finally { setClockingOut(false); }
  };

  useEffect(() => {
    if (workOrderId) {
      fetchTimeLogs();
      // Fetch the billing rule so we know if there's a fixed rate or need to ask
      api.get(`/work-orders/${workOrderId}/billing-rule-codes`)
        .then(res => setBillingRule(res.data || null))
        .catch(() => setBillingRule(null));
    }
  }, [workOrderId]); // eslint-disable-line

  const resetForm = () => {
    setForm({
      log_date: dayjs().format("YYYY-MM-DD"),
      start_time: "",
      end_time: "",
      hours: "",
      description: "",
      is_billable: true,
      manual_billing_amount: "",
    });
    setEditingLog(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setOpenDialog(true);
  };

  const openEditDialog = (log) => {
    setEditingLog(log);
    setForm({
      log_date: log.log_date ? dayjs(log.log_date).format("YYYY-MM-DD") : "",
      start_time: log.start_time
        ? dayjs(log.start_time).format("YYYY-MM-DDTHH:mm")
        : "",
      end_time: log.end_time
        ? dayjs(log.end_time).format("YYYY-MM-DDTHH:mm")
        : "",
      hours: log.hours ?? "",
      description: log.description || "",
      is_billable: Boolean(log.is_billable),
      manual_billing_amount: "",
    });
    setOpenDialog(true);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const computedHours = useMemo(() => {
    if (form.start_time && form.end_time) {
      const start = dayjs(form.start_time);
      const end = dayjs(form.end_time);

      if (start.isValid() && end.isValid() && end.isAfter(start)) {
        const mins = end.diff(start, "minute");
        return (mins / 60).toFixed(2);
      }
    }
    return "";
  }, [form.start_time, form.end_time]);

  useEffect(() => {
    if (computedHours) {
      setForm((prev) => ({
        ...prev,
        hours: computedHours,
      }));
    }
  }, [computedHours]);

  const validateForm = () => {
    if (!currentUserId) {
      showSnackbar("❌ Unable to detect logged-in user.", "error");
      return false;
    }

    if (!form.log_date) {
      showSnackbar("❌ Please select a log date.", "error");
      return false;
    }

    if (!form.hours || Number(form.hours) <= 0) {
      showSnackbar("❌ Hours must be greater than 0.", "error");
      return false;
    }

    return true;
  };

  // Determine the effective hourly rate for billing:
  //  1. Rule has a fixed time_rate → use that (authoritative)
  //  2. Rule exists but no fixed rate → user entered manual_billing_amount
  //  3. No rule → user entered manual_billing_amount
  const effectiveHourlyRate = () => {
    const hrs = parseFloat(form.hours) || 0;
    if (billingRule?.bill_time && billingRule?.time_rate != null) {
      return billingRule.time_rate; // always use rule rate
    }
    if (form.is_billable && form.manual_billing_amount !== "") {
      // Convert the entered total amount back to a per-hour rate for storage
      return hrs > 0 ? parseFloat(form.manual_billing_amount) / hrs : 0;
    }
    return null; // no billing info
  };

  const needsManualAmount = form.is_billable
    && billingRule !== undefined   // rule fetch has resolved
    && (!billingRule?.bill_time || billingRule?.time_rate == null);

  const handleSave = async () => {
    if (!validateForm()) return;

    // If billable and no fixed rule rate, require a manual amount
    if (needsManualAmount && form.is_billable && form.manual_billing_amount === "") {
      showSnackbar("❌ No fixed billing rate found. Please enter the billing amount.", "error");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        work_order_id: Number(workOrderId),
        user_id: Number(currentUserId),
        log_date: form.log_date,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        hours: Number(form.hours),
        description: form.description?.trim() || null,
        is_billable: Boolean(form.is_billable),
        hourly_rate: effectiveHourlyRate(),   // always from rule, never typed per-log
        created_by: currentUserId,
        updated_by: currentUserId,
      };

      if (editingLog?.time_log_id) {
        await api.put(`/workorder-time-logs/${editingLog.time_log_id}`, payload);
        showSnackbar("✅ Time log updated successfully.");
      } else {
        await api.post("/workorder-time-logs", payload);
        showSnackbar("✅ Time log added successfully.");
      }

      setOpenDialog(false);
      resetForm();
      fetchTimeLogs();
    } catch (err) {
      console.error("❌ Failed to save time log:", err);
      showSnackbar("❌ Failed to save time log.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (timeLogId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this time log?"
    );
    if (!confirmed) return;

    try {
      await api.delete(`/workorder-time-logs/${timeLogId}`);
      showSnackbar("✅ Time log deleted successfully.");
      fetchTimeLogs();
    } catch (err) {
      console.error("❌ Failed to delete time log:", err);
      showSnackbar("❌ Failed to delete time log.", "error");
    }
  };

  const resolveUserName = (log) => {
    if (Number(log.user_id) === Number(currentUserId)) return "You";
    return `User #${log.user_id}`;
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "240px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <Card
            sx={{
              ...pageCardSx,
              p: { xs: 1.5, sm: 2, md: 2.5 },
              borderTop: "4px solid #2f63ad",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
                >
                  Time Logs & Billing
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Track labor time, billable hours, and estimated billing totals
                  for this work order.
                </Typography>
              </Box>

              {(canEdit || hasPermission("manage_work_order_time_logs")) && (
                <Box sx={{ display:"flex", gap:1 }}>
                  {clockedIn ? (
                    <Button variant="contained" startIcon={clockingOut ? null : <TimerOffIcon />}
                      onClick={handleClockOut} disabled={clockingOut}
                      sx={{ ...primaryButtonSx, bgcolor:"#dc2626", "&:hover":{ bgcolor:"#b91c1c" } }}>
                      {clockingOut ? "Clocking out..." : "Clock Out"}
                    </Button>
                  ) : (
                    <Button variant="outlined" startIcon={<TimerIcon />} onClick={handleClockIn}
                      sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, borderColor:"#16a34a", color:"#16a34a" }}>
                      Clock In
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openCreateDialog}
                    sx={primaryButtonSx}
                  >
                    Add Time Log
                  </Button>
                </Box>
              )}
            </Box>

            <Grid container spacing={2} sx={{ mt: 1.5 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    borderRadius: "16px",
                    p: 2,
                    boxShadow: "none",
                    border: "1px solid rgba(47,99,173,0.08)",
                    background:
                      "linear-gradient(180deg, rgba(47,99,173,0.05) 0%, rgba(255,255,255,1) 100%)",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                    Total Entries
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>
                    {summary.total_entries || 0}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    borderRadius: "16px",
                    p: 2,
                    boxShadow: "none",
                    border: "1px solid rgba(47,99,173,0.08)",
                    background:
                      "linear-gradient(180deg, rgba(47,99,173,0.05) 0%, rgba(255,255,255,1) 100%)",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                    Total Hours
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>
                    {Number(summary.total_hours || 0).toFixed(2)}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    borderRadius: "16px",
                    p: 2,
                    boxShadow: "none",
                    border: "1px solid rgba(47,99,173,0.08)",
                    background:
                      "linear-gradient(180deg, rgba(47,99,173,0.05) 0%, rgba(255,255,255,1) 100%)",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                    Billable Hours
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>
                    {Number(summary.billable_hours || 0).toFixed(2)}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    borderRadius: "16px",
                    p: 2,
                    boxShadow: "none",
                    border: "1px solid rgba(47,99,173,0.08)",
                    background:
                      "linear-gradient(180deg, rgba(47,99,173,0.05) 0%, rgba(255,255,255,1) 100%)",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                    Estimated Billing
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>
                    ${Number(summary.total_billable_amount || 0).toFixed(2)}
                  </Typography>
                  {summary.billing_rule_rate != null ? (
                    <Typography variant="caption" sx={{ color: "#2f63ad", fontWeight: 600, display: "block", mt: 0.5 }}>
                      @ ${Number(summary.billing_rule_rate).toFixed(2)}/hr
                      {summary.billing_code ? ` · ${summary.billing_code}` : ""}
                      {" "}(billing rule)
                    </Typography>
                  ) : (
                    <Typography variant="caption" sx={{ color: "#6b778c", display: "block", mt: 0.5 }}>
                      @ per-log rates
                    </Typography>
                  )}
                </Card>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ ...pageCardSx, p: { xs: 1.5, sm: 2, md: 2.5 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <ReceiptLongOutlinedIcon sx={{ color: "#2f63ad" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                Logged Entries
              </Typography>
            </Box>

            <Divider sx={{ mb: 2.5, borderColor: "rgba(47,99,173,0.08)" }} />

            {timeLogs.length === 0 ? (
              <Box
                sx={{
                  border: "1px dashed rgba(47,99,173,0.18)",
                  borderRadius: "16px",
                  px: 3,
                  py: 5,
                  textAlign: "center",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 38, color: "#8ba3c7", mb: 1 }} />
                <Typography sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
                  No time logs yet
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
                  Start tracking work performed for this ticket by adding the first time log.
                </Typography>
                {(canEdit || hasPermission("manage_work_order_time_logs")) && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openCreateDialog}
                    sx={primaryButtonSx}
                  >
                    Add First Time Log
                  </Button>
                )}
              </Box>
            ) : (
              <Grid container spacing={2}>
                {timeLogs.map((log) => (
                  <Grid item xs={12} key={log.time_log_id}>
                    <Card
                      sx={{
                        borderRadius: "16px",
                        border: "1px solid rgba(47,99,173,0.08)",
                        boxShadow: "none",
                        p: 2,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: "rgba(47,99,173,0.18)",
                          boxShadow: "0 10px 24px rgba(47,99,173,0.08)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: { xs: "flex-start", md: "center" },
                          flexDirection: { xs: "column", md: "row" },
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                              mb: 0.75,
                            }}
                          >
                            <Chip
                              label={dayjs(log.log_date).format("MMM D, YYYY")}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                backgroundColor: "rgba(47,99,173,0.10)",
                                color: "#2f63ad",
                              }}
                            />

                            <Chip
                              label={`${Number(log.hours || 0).toFixed(2)} hrs`}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                backgroundColor: "rgba(34,49,66,0.08)",
                                color: "#223142",
                              }}
                            />

                            <Chip
                              label={log.is_billable ? "Billable" : "Non-Billable"}
                              size="small"
                              color={log.is_billable ? "success" : "default"}
                              variant={log.is_billable ? "filled" : "outlined"}
                            />
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              mb: 0.75,
                              color: "#5f6b7a",
                            }}
                          >
                            <PersonOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {resolveUserName(log)}
                            </Typography>
                          </Box>

                          {(log.start_time || log.end_time) && (
                            <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                              {log.start_time
                                ? dayjs(log.start_time).format("MMM D, YYYY h:mm A")
                                : "N/A"}
                              {"  "}→{"  "}
                              {log.end_time
                                ? dayjs(log.end_time).format("MMM D, YYYY h:mm A")
                                : "N/A"}
                            </Typography>
                          )}

                          <Typography
                            variant="body2"
                            sx={{
                              color: "#223142",
                              whiteSpace: "pre-wrap",
                              lineHeight: 1.65,
                            }}
                          >
                            {log.description || "No description provided."}
                          </Typography>
                        </Box>

                        {(canEdit || hasPermission("manage_work_order_time_logs")) && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignSelf: { xs: "flex-end", md: "flex-start" },
                            }}
                          >
                            <IconButton
                              onClick={() => openEditDialog(log)}
                              sx={{
                                border: "1px solid rgba(47,99,173,0.14)",
                                borderRadius: "12px",
                                color: "#2f63ad",
                              }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>

                            <IconButton
                              onClick={() => handleDelete(log.time_log_id)}
                              sx={{
                                border: "1px solid rgba(220,0,78,0.14)",
                                borderRadius: "12px",
                                color: "#d32f2f",
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#223142" }}>
          {editingLog ? "Edit Time Log" : "Add Time Log"}
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid item xs={12}>
              <TextField
                label="Log Date"
                type="date"
                fullWidth
                value={form.log_date}
                onChange={(e) => handleChange("log_date", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Time"
                type="datetime-local"
                fullWidth
                value={form.start_time}
                onChange={(e) => handleChange("start_time", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="End Time"
                type="datetime-local"
                fullWidth
                value={form.end_time}
                onChange={(e) => handleChange("end_time", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Hours"
                type="number"
                fullWidth
                value={form.hours}
                onChange={(e) => handleChange("hours", e.target.value)}
                inputProps={{ min: 0, step: 0.25 }}
                sx={textFieldSx}
                helperText="Auto-calculated if start and end time are provided."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                minRows={4}
                value={form.description}
                onChange={(e) => handleChange("description", e.target.value)}
                sx={textFieldSx}
                placeholder="Describe the work performed..."
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_billable}
                    onChange={(e) => handleChange("is_billable", e.target.checked)}
                  />
                }
                label="Billable"
              />
            </Grid>

            {/* Show billing rate info or ask for amount when no fixed rule rate */}
            {form.is_billable && billingRule?.bill_time && billingRule?.time_rate != null && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: "10px" }}>
                  Billing rate from rule: <strong>${Number(billingRule.time_rate).toFixed(2)}/hr</strong>
                  {billingRule.time_billing_code ? ` · Code: ${billingRule.time_billing_code}` : ""}
                  {form.hours ? ` → $${(Number(form.hours) * billingRule.time_rate).toFixed(2)} total` : ""}
                </Alert>
              </Grid>
            )}

            {form.is_billable && needsManualAmount && (
              <Grid item xs={12}>
                <TextField
                  label="Billing Amount ($) *"
                  type="number"
                  fullWidth
                  value={form.manual_billing_amount}
                  onChange={(e) => handleChange("manual_billing_amount", e.target.value)}
                  inputProps={{ min: 0, step: "0.01" }}
                  sx={textFieldSx}
                  helperText="No fixed billing rate found in settings. Enter the amount to bill for this time log."
                  error={form.manual_billing_amount === ""}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            variant="outlined"
            sx={secondaryButtonSx}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={primaryButtonSx}
          >
            {saving
              ? "Saving..."
              : editingLog
              ? "Update Time Log"
              : "Save Time Log"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 3 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TimeLogsTab;